import React, { useEffect, useRef, useState } from 'react'
import { Button, useTheme } from '@mui/material'
import clsx from 'clsx'
import StorageIcon from '@mui/icons-material/Storage'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import Text from '../../Text'
import LinkWithQuery from '../../LinkWithQuery'
import { traduzirMotivo } from '../../../libs/relatorioMotivos'
import { corLegivel } from '../../../libs/corLegivel'
import metaWhite from '../../../assets/meta-white-color.png'
import metaPrimary from '../../../assets/meta-primary-color.png'
import LinhaDeBatimento from './LinhaDeBatimento'
import {
  ESTADOS,
  CORES,
  subtitulo,
  textoExpediente,
  haQuanto,
  previsaoTermino,
  mascararTelefone,
  horaDoEvento,
  idadeDaFila,
} from './estado'
import './aoVivo.css'

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('pt-BR') : n)

// Re-renderiza só quem mostra "há Xs", não a página inteira, a cada segundo.
function HaQuanto({ iso, prefixo = '' }) {
  const [, setTique] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTique((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])
  return <>{prefixo}{haQuanto(iso)}</>
}

function useCores() {
  const theme = useTheme()
  return {
    theme,
    borda: theme.isDark ? '#334155' : '#e2e8f0',
    fundo: theme.palette.background.paper,
    suave: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    apagado: theme.isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
    fraco: theme.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.42)',
    pri: theme.palette.primary.main,
    // Cor do texto nos contêineres: os <span>/<li> soltos herdam dela. Sem
    // isso eles caíam na cor padrão do body (escura) e sumiam no tema escuro.
    texto: theme.palette.text.primary,
    // Cor de destaque usada como texto, ajustada pra ser lida sobre o card em
    // qualquer tema (o ciano da marca some sobre branco). `minimo` 3 é pra
    // número grande/negrito; o padrão 4,5 é pra texto pequeno.
    legivel: (cor, minimo) => corLegivel(cor, theme.palette.background.paper, minimo),
  }
}

function Cartao({ children, className = '', style }) {
  const c = useCores()
  return (
    <div
      className={clsx('rounded-[14px] px-4 py-3.5 min-w-0', className)}
      style={{ border: `1px solid ${c.borda}`, background: c.fundo, color: c.texto, ...style }}
    >
      {children}
    </div>
  )
}

function Rotulo({ children }) {
  return <Text className="text-[10.5px] font-bold uppercase tracking-[0.09em] opacity-55">{children}</Text>
}

// ---------------------------------------------------------------------------
// Conexão: farol, ritmo e a linha de batimento
// ---------------------------------------------------------------------------
function CartaoConexao({ pulso, visual, metaOficial, pausado }) {
  const c = useCores()
  const ritmo = pulso?.ritmo
  const estado = pulso?.estado
  const corLinha = pausado ? CORES.neutro : visual.cor

  return (
    <Cartao>
      <div className="flex items-center gap-3">
        <span className="av-farol"><i /></span>
        <div className="min-w-0">
          <Text className="text-[14.5px] font-extrabold leading-tight">{visual.titulo}</Text>
          <Text className="text-[11.5px] mt-0.5" style={{ color: c.apagado }}>{subtitulo(pulso, { metaOficial })}</Text>
        </div>
        <div className="ml-auto text-right leading-none">
          <Text className="text-[26px] font-black block" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {ritmo ? fmt(ritmo.porMinuto) : '—'}
          </Text>
          <Text className="text-[10.5px] font-semibold" style={{ color: c.apagado }}>msg/min</Text>
        </div>
      </div>
      <div className="mt-2.5">
        <LinhaDeBatimento
          saidas={ritmo?.saidas}
          cor={corLinha}
          grade={c.suave}
          animado={!pausado && (estado === 'enviando' || estado === 'ociosa')}
          tracejado={estado === 'desconectada'}
        />
      </div>
      <div className="mt-1.5 flex flex-wrap justify-between gap-x-3 gap-y-1 text-[11.5px]" style={{ color: c.apagado }}>
        <span>
          Última saída{' '}
          <b style={{ color: c.theme.palette.text.primary }}>
            {ritmo?.ultimaSaidaEm ? <HaQuanto iso={ritmo.ultimaSaidaEm} /> : 'nenhuma recente'}
          </b>
        </span>
        <span>
          Expediente <b style={{ color: c.theme.palette.text.primary }}>{textoExpediente(pulso?.expediente)}</b>
        </span>
      </div>
      {estado === 'desconectada' && (
        <LinkWithQuery to="/" className="inline-block mt-2.5">
          <span className="inline-block rounded-lg px-3 py-1.5 text-xs font-bold text-white" style={{ background: CORES.erro }}>
            Reconectar instância
          </span>
        </LinkWithQuery>
      )}
      {estado === 'foraDoExpediente' && (
        <LinkWithQuery to="/messages/scheduler" className="inline-block mt-2 text-xs font-bold" style={{ color: c.legivel(c.pri) }}>
          Configurar expediente
        </LinkWithQuery>
      )}
    </Cartao>
  )
}

// ---------------------------------------------------------------------------
// Fila em andamento
// ---------------------------------------------------------------------------
function CartaoFila({ pulso, pausado }) {
  const c = useCores()
  const fila = pulso?.fila
  const andando = fila?.andando || 0
  // A barra mostra quanto da fila já foi: o cheio é o maior tamanho visto
  // desde que ela começou a encher, e ela esvazia conforme as mensagens saem.
  const pico = useRef(0)
  if (andando === 0) pico.current = 0
  else pico.current = Math.max(pico.current, andando)
  const largura = andando ? Math.max(4, (andando / pico.current) * 100) : 0

  const retidas = (fila?.retidasSmartFilter || 0) + (fila?.aguardandoConferencia || 0) + (fila?.retidasHiperpay || 0)
  const retidasSaldo = fila?.retidasSaldo || 0
  const estado = pulso?.estado
  const ultimoModelo = (pulso?.eventos || []).find((e) => e.tipo === 'enviada' && e.modelo)?.modelo

  let agora = 'Nada na fila agora'
  if (pausado) agora = 'Atualização pausada'
  else if (estado === 'desconectada') agora = 'Fila parada: instância desconectada'
  else if (estado === 'foraDoExpediente') agora = 'Fila retida até abrir o expediente'
  else if (estado === 'limiteDiario') agora = 'Fila retida pelo teto diário'
  else if (estado === 'parada') agora = 'Aguardando a próxima saída'
  else if (andando > 0) agora = ultimoModelo ? `Enviando ${ultimoModelo}` : 'Enviando a fila'

  // Há quanto tempo a mais antiga espera. Fila que anda rápido mostra "há 2
  // min" e ninguém se preocupa; já uma de ontem é sinal de que algo está
  // segurando — e com 24h a mensagem é descartada por idade.
  const idade = andando > 0 ? idadeDaFila(fila?.maisAntigaEm, pulso?.agora ? new Date(pulso.agora) : new Date()) : null
  const corIdade = idade?.atencao ? c.legivel(CORES.atencao) : c.theme.palette.text.primary

  return (
    <Cartao style={{ '--c': CORES.atencao }}>
      <Rotulo>Fila em andamento</Rotulo>
      <div className="flex items-baseline gap-1.5 mt-1.5">
        <Text className="text-[34px] font-black leading-none" style={{ color: andando ? c.legivel(CORES.atencao, 3) : c.fraco, fontVariantNumeric: 'tabular-nums' }}>
          {fila ? fmt(andando) : '—'}
        </Text>
        <Text className="text-xs" style={{ color: c.apagado }}>{andando === 1 ? 'mensagem aguardando' : 'mensagens aguardando'}</Text>
      </div>
      <div className="av-fila-trilho mt-3" style={{ background: c.suave }}>
        <div className="av-fila-barra" style={{ width: `${largura}%` }} />
      </div>
      <div className="mt-2 flex justify-between gap-2 text-[11.5px]" style={{ color: c.apagado }}>
        <span>Termina em <b style={{ color: c.theme.palette.text.primary }}>{previsaoTermino(andando, pulso?.ritmo?.porMinuto)}</b></span>
        {(retidas > 0 || retidasSaldo > 0) && (
          <span title="Esperando uma regra do SmartFilter, saldo ou o link de pagamento (HiperPay) — saem sozinhas quando liberadas">
            + <b style={{ color: c.theme.palette.text.primary }}>{fmt(retidas + retidasSaldo)}</b> retidas
          </span>
        )}
      </div>
      {idade && (
        <Text
          data-testid="fila-idade"
          className={clsx('mt-1.5 block text-[11.5px]', { 'font-semibold': idade.atencao })}
          style={{ color: idade.atencao ? corIdade : c.apagado }}
          title={idade.atencao ? 'Mensagem que passa de 24h na fila é descartada para não chegar atrasada' : undefined}
        >
          {idade.atencao && '⚠ '}A mais antiga está na fila <b style={{ color: corIdade }}>{idade.texto}</b>
        </Text>
      )}
      {estado === 'limiteDiario' && !pausado && (
        <Text data-testid="fila-teto" className="mt-1 block text-[11.5px]" style={{ color: c.apagado }}>
          As mensagens adiadas pelo teto diário voltam a sair no dia seguinte, pela manhã.
        </Text>
      )}
      <div className="mt-2.5 flex items-center gap-2 text-[11.5px] min-w-0" style={{ color: c.apagado, '--c': c.pri }}>
        <span className="av-pontinhos"><i /><i /><i /></span>
        <span className="truncate">{agora}</span>
      </div>
    </Cartao>
  )
}

// ---------------------------------------------------------------------------
// Agora mesmo: os últimos acontecimentos, entrando um a um
// ---------------------------------------------------------------------------
const VISUAL_EVENTO = {
  enviada: { icone: '↑', cor: CORES.ok },
  entregue: { icone: '✓', cor: '#00a9d6' },
  lida: { icone: '✓', cor: '#2563eb' },
  erro: { icone: '✕', cor: CORES.erro },
  lote: { icone: '+', cor: CORES.atencao },
}

// O motivo da falha segue a mesma regra das análises e do PDF (por provider):
// na conexão QR Code o servidor já manda o motivo real em português no
// erro.text, e passá-lo pelo dicionário da Meta transformava, por exemplo, uma
// expiração na fila em "Falha no envio pela Meta". Só a API Oficial usa a
// tradução da Meta.
export function descricaoEvento(e, provider) {
  if (e.tipo === 'lote') return e.texto
  if (e.tipo === 'erro') {
    const erro = e.erro || {}
    return traduzirMotivo({ erro, chave: erro.code || erro.text }, provider).titulo || 'falhou'
  }
  if (e.tipo === 'entregue') return 'entregue'
  if (e.tipo === 'lida') return 'lida'
  return e.modelo || 'enviada'
}


function CartaoAgora({ pulso, pausado, provider }) {
  const c = useCores()
  const [lista, setLista] = useState([])
  const vistos = useRef(new Set())
  const timers = useRef([])
  const ultimoLote = useRef(0)

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  useEffect(() => {
    if (!pulso) return
    const eventos = [...(pulso.eventos || [])]

    // Muita coisa entrando pela API vira um aviso de "lote" (no máximo 1/min).
    const entradas = pulso.entradas?.ultimoMinuto || 0
    if (entradas >= 10 && Date.now() - ultimoLote.current > 60000) {
      ultimoLote.current = Date.now()
      eventos.push({
        id: `lote:${pulso.agora}`,
        tipo: 'lote',
        em: pulso.agora,
        texto: `Recebendo ${fmt(entradas)} mensagens por minuto via API`,
      })
    }

    const novos = eventos
      .filter((e) => !vistos.current.has(e.id))
      .sort((a, b) => new Date(a.em) - new Date(b.em))
    novos.forEach((e) => vistos.current.add(e.id))
    if (!novos.length) return

    // Primeira carga: a lista aparece inteira, sem cascata.
    if (lista.length === 0) {
      setLista(novos.reverse().slice(0, 8))
      return
    }

    // Depois, cada novidade entra no seu tempo, espalhada entre as batidas —
    // o feed parece contínuo mesmo com a consulta a cada 5s.
    const passo = pausado ? 0 : Math.min(900, 4500 / novos.length)
    novos.forEach((e, i) => {
      const t = setTimeout(() => {
        setLista((atual) => [{ ...e, novo: true }, ...atual].slice(0, 8))
        const apaga = setTimeout(() => {
          setLista((atual) => atual.map((x) => (x.id === e.id ? { ...x, novo: false } : x)))
        }, 1400)
        timers.current.push(apaga)
      }, i * passo)
      timers.current.push(t)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulso])

  return (
    <Cartao>
      <Rotulo>Agora mesmo</Rotulo>
      {lista.length === 0 ? (
        <Text className="text-xs mt-3" style={{ color: c.apagado }}>
          {pulso ? 'Nenhum envio recente nesta instância.' : 'Carregando…'}
        </Text>
      ) : (
        <ol className="relative mt-2 grid gap-0.5 overflow-hidden" style={{ maxHeight: 152 }}>
          {lista.map((e) => {
            const v = VISUAL_EVENTO[e.tipo] || VISUAL_EVENTO.enviada
            return (
              <li
                key={e.id}
                className={clsx('av-evento grid items-center gap-2 rounded-lg px-1.5 py-1 text-xs', { 'av-evento-novo': e.novo })}
                style={{ gridTemplateColumns: '18px minmax(0,1fr) auto', '--c': v.cor }}
              >
                <span
                  className="grid place-items-center rounded-full text-[10px] font-black"
                  style={{ width: 18, height: 18, color: c.legivel(v.cor, 3), background: `${v.cor}2e` }}
                >
                  {e.tipo === 'lida' ? '✓✓' : v.icone}
                </span>
                <span className="truncate">
                  {e.tipo !== 'lote' && <b>{mascararTelefone(e.telefone)} </b>}
                  <span style={{ color: c.apagado }}>{e.tipo !== 'lote' ? '· ' : ''}{descricaoEvento(e, provider)}</span>
                </span>
                <time className="text-[10.5px]" style={{ color: c.fraco, fontVariantNumeric: 'tabular-nums' }}>{horaDoEvento(e.em)}</time>
              </li>
            )
          })}
          <span
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8"
            style={{ background: `linear-gradient(transparent, ${c.fundo})` }}
          />
        </ol>
      )}
    </Cartao>
  )
}

// ---------------------------------------------------------------------------
// Contadores do período, com o "+N" quando mudam
// ---------------------------------------------------------------------------
function Contador({ rotulo, valor, cor, dica }) {
  const c = useCores()
  const anterior = useRef(valor)
  const [delta, setDelta] = useState(null)

  useEffect(() => {
    const antes = anterior.current
    anterior.current = valor
    if (typeof antes === 'number' && typeof valor === 'number' && valor > antes) {
      setDelta({ n: valor - antes, chave: Date.now() })
    }
  }, [valor])

  return (
    <div
      key={delta ? delta.chave : 'k'}
      className={clsx('rounded-xl flex items-stretch gap-3 p-3 min-w-0', { 'av-kpi-pisca': !!delta })}
      style={{ border: `1px solid ${c.borda}`, background: c.fundo, color: c.texto, '--c': c.legivel(cor, 3) }}
    >
      <span className="w-[3px] shrink-0 rounded-full" style={{ background: cor }} />
      <div className="min-w-0">
        <Rotulo>{rotulo}</Rotulo>
        {valor === undefined ? (
          <div className="mt-1 h-6 w-16 rounded bg-current opacity-10 animate-pulse" />
        ) : (
          <Text className="text-[22px] font-black leading-tight mt-0.5" style={{ color: c.legivel(cor, 3), fontVariantNumeric: 'tabular-nums' }}>
            {fmt(valor)}
            {delta && <span key={delta.chave} className="av-delta">+{fmt(delta.n)}</span>}
          </Text>
        )}
        {dica && <Text className="text-[11px] opacity-60 block truncate">{dica}</Text>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Topo completo
// ---------------------------------------------------------------------------
export default function PainelAoVivo({
  api,
  server,
  provider,
  metaOficial,
  pulso,
  atualizadoEm,
  falhou,
  pausado,
  onAlternarPausa,
  seletorDePeriodo,
  statistics,
  periodLabel,
}) {
  const c = useCores()
  const theme = c.theme
  const visual = ESTADOS[pulso?.estado] || ESTADOS.ociosa
  const cor = pausado ? CORES.neutro : visual.cor
  const selo = pausado ? 'PAUSADO' : falhou && !pulso ? 'SEM SINAL' : visual.selo
  const animado = !pausado && visual.animado
  // Master ainda sem a rota do pulso (ou fora do ar): o topo não inventa
  // estado — some o deck ao vivo e ficam só os contadores.
  const semPulso = falhou && !pulso

  const entregues = statistics.received === undefined ? undefined : (statistics.received || 0) + (statistics.seen || 0)

  return (
    <div
      className={clsx({ 'av-animado': animado, 'av-alarme': !pausado && pulso?.estado === 'desconectada' })}
      style={{ '--st': cor, color: c.texto }}
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="rounded-xl flex items-center justify-center shrink-0"
            style={{ width: 46, height: 46, background: theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}
          >
            <StorageIcon style={{ fontSize: 24, opacity: 0.6 }} />
          </div>
          <div className="min-w-0">
            {!provider ? (
              <span className="block bg-gray-500 opacity-[0.2] h-[16px] w-[80px]" />
            ) : metaOficial ? (
              <div className="flex items-center space-x-2">
                <Text className="text-xs font-semibold opacity-50 uppercase tracking-wider">API Oficial</Text>
                <img src={theme.isDark ? metaWhite : metaPrimary} className="w-[52px]" alt="Meta" />
              </div>
            ) : (
              <Text className="text-xs font-semibold opacity-50 uppercase tracking-wider">API</Text>
            )}
            <Text className="text-sm md:text-lg font-bold truncate">{api}</Text>
            <Text className="text-xs opacity-60 flex items-center gap-1">
              <span>Servidor</span>
              {server !== '' ? <span className="font-semibold">{server}</span> : <span className="inline-block h-[12px] w-[16px] bg-current opacity-20 rounded" />}
            </Text>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-3">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="av-pill" style={{ color: c.legivel(cor) }}><span className="av-pill-dot" />{selo}</span>
            {atualizadoEm && (
              <Text component="span" className="text-[11.5px]" style={{ color: c.fraco }}>
                {pausado ? 'pausado' : <HaQuanto iso={atualizadoEm} prefixo="atualizado " />}
              </Text>
            )}
            <Button
              onClick={onAlternarPausa}
              size="small"
              color="inherit"
              startIcon={pausado ? <PlayArrowIcon style={{ fontSize: 16 }} /> : <PauseIcon style={{ fontSize: 16 }} />}
              sx={{ borderRadius: '9px', textTransform: 'none', fontWeight: 600, fontSize: 12, border: `1px solid ${c.borda}`, py: 0.25, color: c.texto }}
            >
              {pausado ? 'Retomar' : 'Pausar'}
            </Button>
          </div>
          {seletorDePeriodo}
        </div>
      </div>

      {!semPulso && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1.35fr_1fr_1.15fr] gap-3">
          <CartaoConexao pulso={pulso} visual={visual} metaOficial={metaOficial} pausado={pausado} />
          <CartaoFila pulso={pulso} pausado={pausado} />
          <CartaoAgora pulso={pulso} pausado={pausado} provider={provider} />
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Contador rotulo="Enviados" valor={statistics.success} cor="#059669" dica={periodLabel} />
        <Contador rotulo="Entregues" valor={entregues} cor={c.pri} dica="chegaram no aparelho" />
        <Contador rotulo="Lidos" valor={statistics.seen} cor="#3b82f6" dica="abertos pelo cliente" />
        <Contador rotulo="Com erro" valor={statistics.error} cor="#eb3c3c" dica={periodLabel} />
      </div>
    </div>
  )
}
